import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Progress } from '../ui/progress';

export const DropZone = ({ onFileSelect, isProcessing, processingStatus }) => {
  const { t } = useTranslation('ocr');
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  }, [onFileSelect]);

  const handleFileInput = (e) => {
    if (e.target.files.length > 0) {
      onFileSelect(e.target.files[0]);
    }
  };

  const getStatusIcon = () => {
    if (!processingStatus) return <Loader2 className="h-16 w-16 mx-auto text-[#E63946] animate-spin" />;
    
    switch (processingStatus.status) {
      case 'completed':
        return <CheckCircle2 className="h-16 w-16 mx-auto text-green-500" />;
      case 'error':
        return <XCircle className="h-16 w-16 mx-auto text-red-500" />;
      default:
        return <Loader2 className="h-16 w-16 mx-auto text-[#E63946] animate-spin" />;
    }
  };

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
        isDragOver 
          ? 'border-[#E63946] bg-[#FDE8EA] dark:bg-[#E63946]/20' 
          : 'border-gray-300 dark:border-gray-600 hover:border-[#E63946] hover:bg-gray-50 dark:hover:bg-gray-800'
      } ${isProcessing ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !isProcessing && document.getElementById('file-input').click()}
      data-testid="ocr-dropzone"
    >
      <input
        id="file-input"
        type="file"
        accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,image/png,image/jpeg,image/gif,image/webp,application/pdf"
        className="hidden"
        onChange={handleFileInput}
        disabled={isProcessing}
      />
      
      {isProcessing ? (
        <div className="space-y-4">
          {getStatusIcon()}
          <p className="text-lg font-medium text-foreground">
            {processingStatus?.message || t('extract.dropzone.processing')}
          </p>
          
          {/* Progress Bar */}
          {processingStatus && processingStatus.status !== 'error' && (
            <div className="max-w-xs mx-auto space-y-2">
              <Progress 
                value={processingStatus.progress || 0} 
                className="h-2"
              />
              <p className="text-xs text-muted-foreground">
                {processingStatus.progress || 0}%
              </p>
            </div>
          )}
          
          <p className="text-sm text-muted-foreground">
            {processingStatus?.status === 'error' 
              ? t('extract.dropzone.errorHint')
              : t('extract.dropzone.processingHint')
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="h-16 w-16 mx-auto rounded-full bg-[#FDE8EA] dark:bg-[#E63946]/20 flex items-center justify-center">
            <Upload className="h-8 w-8 text-[#E63946]" />
          </div>
          <div>
            <p className="text-lg font-medium text-foreground">
              {t('extract.dropzone.title')}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('extract.dropzone.formats')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropZone;
